import { useState, useEffect } from 'react'
import {
  handleSignedAgreement,
  updateSignedLease,
  getLeaseByContractId,
  preparePayment
} from '../../../../api-utils'
import {
  calculateProratedTotal,
  calculateProratedRent,
  calculateMonthlyRent,
  formatCurrency
} from '../../../../utils'
import {
  PAYMENT_CONSTANTS,
  PaymentLineItem,
  PaymentInvoiceData
} from '../../../../payment-utils'

const {
  PREP_PAYMENT_TITLE,
  PREP_PAYMENT_SUBTITLE
} = PAYMENT_CONSTANTS

/**
 * Hook to handle the signed agreement flow:
 * 1. Fetch signed PDF from eSignatures.io
 * 2. Update lease with signed PDF
 * 3. Fetch lease with expanded customer/vehicle data
 * 4. Prepare payment invoice with line items
 * 5. Get checkout token for Helcim payment
 *
 * @param hasSigned - Whether the agreement has been signed
 * @param space - The space object with pricing details
 * @param contractId - The eSignatures.io contract ID
 * @returns Object with checkoutToken, page title/subtitle, and error
 */
export function useSignedAgreementFlow(
  hasSigned: boolean,
  space: any,
  contractId: string | null,
  // Optional pro-ration anchor. Defaults to today (live reservations). The
  // /complete flow passes the customer's original reservation date so a late
  // completion is still pro-rated from when they reserved. See
  // proration-anchor-policy.
  prorationDate?: Date
) {
  const [checkoutToken, setCheckoutToken] = useState<string>()
  const [pageTitle, setPageTitle] = useState<string>()
  const [pageSubtitle, setPageSubtitle] = useState<string>()
  const [error, setError] = useState<string>()

  useEffect(() => {
    if (hasSigned && space && contractId) {
      handleSignedAgreement(contractId)
        .then((contractResponse: any) => {
          const signedPDF = contractResponse.contract_pdf_url

          // Get current time in Eastern timezone
          const now = new Date()
          const easternTime = new Date(now.toLocaleString("en-US", { timeZone: "America/New_York" }))

          // Update lease with signed PDF using Netlify function
          return updateSignedLease(contractId, signedPDF, easternTime.toISOString())
        })
        .then((updateResponse: any) => {
          if (!updateResponse.success) {
            throw new Error(updateResponse.error || 'Failed to update lease with signed PDF')
          }

          // Query lease for payment preparation (need vehicle and customer data)
          // Use Netlify function to fetch lease with expanded customer and vehicles
          return getLeaseByContractId(contractId)
        })
        .then((leaseResponse: any) => {
          if (!leaseResponse.success) {
            throw new Error(leaseResponse.error || 'Failed to fetch lease data')
          }

          const matchingLease = leaseResponse.lease
          const dataCustomer = matchingLease.expand?.customer
          const vehicles = matchingLease.expand?.vehicles || []

          console.log(">> GOT LEASE WITH EXPANDED DATA! ", matchingLease)
          console.log(">> GOT CUSTOMER! ", dataCustomer)

          // Build vehicle notes from expanded vehicles
          const notes: Array<string> = []
          vehicles.forEach((vehicleData: any) => {
            console.log(" GOT VEHICLE >>> ", vehicleData)
            notes.push(
              `${vehicleData.type} ${vehicleData.year} ${vehicleData.make} ${vehicleData.model} - ${vehicleData.lic_plate_state} Plate#: ${vehicleData.lic_plate_number};`
            )
            if (vehicleData.description && vehicleData.description.length > 0) {
              notes.push(`Description: ${vehicleData.description}`)
            }
          })
          console.log("VEHICLE DESC", notes)

          // Get gate options from the lease record (most reliable source)
          const leaseHas24hrAccess = matchingLease.options && matchingLease.options.includes('access-24-hrs')
          const leaseHas3xWeekAccess = matchingLease.options && matchingLease.options.includes('access-3x-week')

          // Calculate pro-rated rent (base + gate options if selected). The
          // anchor date defaults to today for live reservations; /complete
          // passes the reservation date so a late completion isn't over/under-charged.
          const anchor = prorationDate || new Date()
          const proRatedTotal = calculateProratedTotal(space.monthly_rate, leaseHas24hrAccess, leaseHas3xWeekAccess, anchor)
          const proRatedMonthlyRate = calculateProratedRent(space.monthly_rate, anchor)

          // Build line items for invoice
          const leaseOptions: Array<PaymentLineItem> = []
          if (leaseHas24hrAccess) {
            // @TODO: use database values and texts here
            const proRated24hrAccess = calculateProratedRent(15, anchor)
            leaseOptions.push({ sku: `access-24-hrs`, description: 'access 24 hrs a day (pro-rated)', quantity: 1, price: proRated24hrAccess, total: proRated24hrAccess })
          }
          if (leaseHas3xWeekAccess) {
            const proRated3xWeeklyAccess = calculateProratedRent(15, anchor)
            leaseOptions.push({ sku: `access-3x-week`, description: 'access 3x week or more (pro-rated)', quantity: 1, price: proRated3xWeeklyAccess, total: proRated3xWeeklyAccess })
          }

          // Deposit should include base monthly rate plus any gate options
          const deposit = calculateMonthlyRent(space.monthly_rate, leaseHas24hrAccess, leaseHas3xWeekAccess)
          const dataInvoice: PaymentInvoiceData = {
            lineItems: [
              { sku: `space-${space.length}ft-${space.type}`, description: `${space.length}ft-${space.type} (pro-rated)`, quantity: 1, price: proRatedMonthlyRate, total: proRatedMonthlyRate },
              { sku: `security-deposit-${space.monthly_rate}`, description: 'security deposit', quantity: 1, price: deposit, total: deposit },
              ...leaseOptions
            ],
            notes: notes.toString()
          }

          console.log(">>> ABOUT TO SEND DATA", {
            amount: formatCurrency(proRatedTotal + deposit),
            customer: dataCustomer,
            invoice: dataInvoice
          })

          // Prepare payment after processing all vehicles
          return preparePayment({
            amount: formatCurrency(proRatedTotal + deposit),
            customer: dataCustomer,
            invoice: dataInvoice
          })
        })
        .then((response: any) => {
          if (response.data.checkoutToken) {
            setCheckoutToken(response.data.checkoutToken)
            setPageTitle(PREP_PAYMENT_TITLE)
            setPageSubtitle(PREP_PAYMENT_SUBTITLE)
            setError(undefined)
          }
        })
        .catch((err: any) => {
          console.error(">>> Error in signed agreement flow:", err)
          setError(`Payment preparation failed: ${err.message || 'Unknown error'}`)
        })
    }
  }, [hasSigned, space, contractId])

  return { checkoutToken, pageTitle, pageSubtitle, error }
}
